import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { LogOut } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Badge, fmtDate } from "@/components/Protected";
import { ReferralFeed } from "@/pages/PartnerPortal";
import { ContactInbox } from "@/components/ContactInbox";
import { ActivityLog } from "@/components/ActivityLog";
import { ApplicationDocsDialog } from "@/components/ApplicationDocs";

const TABS = ["applications", "inbox", "referrals", "orders", "partners", "activity"];
const TAB_LABEL = { partners: "Users & access", inbox: "Contact inbox", activity: "Activity log" };
const APP_STATUSES = ["received", "underwriting", "pending_docs", "approved", "declined"];

function Applications({ items, setItems }) {
  const [docsFor, setDocsFor] = useState(null);
  const setStatus = async (id, status) => {
    try {
      await api.patch(`/applications/${id}/status`, { status });
      setItems(items.map((a) => (a.id === id ? { ...a, status } : a)));
      toast.success("Status updated");
    } catch (e) { toast.error(errMsg(e)); }
  };
  if (!items.length) return <p className="text-slate-400" data-testid="apps-empty">No applications yet.</p>;
  return (
    <div className="overflow-x-auto border border-white/10" data-testid="apps-table">
      <table className="w-full min-w-[820px] text-left text-sm">
        <thead className="font-mono text-[10px] uppercase tracking-widest text-slate-400">
          <tr className="border-b border-white/10">{["Ref", "Business", "Industry", "Volume", "Jurisdictions", "Docs", "Date", "Status"].map((h) => <th key={h} className="px-4 py-3 font-normal">{h}</th>)}</tr>
        </thead>
        <tbody>
          {items.map((a) => (
            <tr key={a.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`app-row-${a.id}`}>
              <td className="px-4 py-4 font-mono text-xs text-[var(--gold)]">{a.reference}</td>
              <td className="px-4 py-4"><p className="font-medium">{a.business_name}</p><p className="text-xs text-slate-400">{a.contact_name} · {a.email}</p></td>
              <td className="px-4 py-4 text-slate-300">{a.industry}</td>
              <td className="px-4 py-4 font-mono text-xs">{a.monthly_volume}</td>
              <td className="px-4 py-4 text-xs text-slate-300">{(a.jurisdictions || []).join(", ")}</td>
              <td className="px-4 py-4">
                <button onClick={() => setDocsFor(a)} data-testid={`app-docs-button-${a.id}`}
                  className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs ${a.doc_count || a.authorized ? "border-sky-400/50 text-sky-300 hover:bg-sky-400/10" : "border-white/15 text-slate-400 hover:border-white/40"}`}>
                  {a.doc_count ?? 0} files{a.authorized ? " · signed" : ""}
                </button>
              </td>
              <td className="px-4 py-4 font-mono text-xs text-slate-400">{fmtDate(a.created_at)}</td>
              <td className="px-4 py-4">
                <select className="field !w-auto !py-1.5 !text-xs" value={a.status} onChange={(e) => setStatus(a.id, e.target.value)} data-testid={`app-status-select-${a.id}`}>
                  {APP_STATUSES.map((s) => <option key={s} value={s}>{s.replace("_", " ")}</option>)}
                </select>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <ApplicationDocsDialog app={docsFor} onClose={() => setDocsFor(null)} />
    </div>
  );
}

function Orders({ items }) {
  if (!items.length) return <p className="text-slate-400" data-testid="orders-empty">No orders yet.</p>;
  return (
    <div className="space-y-3" data-testid="orders-list">
      {items.map((o) => (
        <div key={o.id} className="flex flex-wrap items-center justify-between gap-4 border border-white/10 bg-[#0d1524] p-5" data-testid={`order-${o.id}`}>
          <div>
            <p className="font-medium">{(o.items || []).map((i) => `${i.quantity}× ${i.name}`).join(", ")}</p>
            <p className="font-mono text-xs text-slate-400">{o.email || "no email"} · {fmtDate(o.created_at)}</p>
          </div>
          <div className="flex items-center gap-4"><span className="font-display text-xl">{money(o.amount)}</span><Badge s={o.payment_status} /></div>
        </div>
      ))}
    </div>
  );
}

function Partners({ items, setItems }) {
  const { user: me } = useAuth();
  const setAccess = async (p, status, role) => {
    try {
      await api.patch(`/admin/users/${p.id}`, { status, role });
      setItems(items.map((x) => (x.id === p.id ? { ...x, status, role } : x)));
      toast.success(status === "active" ? (p.status !== "active" ? `Approved as ${role} · welcome email sent` : `Updated to ${role}`) : "Access removed");
    } catch (e) { toast.error(errMsg(e)); }
  };
  const [f, setF] = useState({ name: "", email: "", company: "FinBPM", password: "" });
  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/admin/partners", f);
      setItems([data, ...items]);
      setF({ name: "", email: "", company: "FinBPM", password: "" });
      toast.success("Partner account created");
    } catch (e2) { toast.error(errMsg(e2)); }
  };
  return (
    <div className="grid gap-8 lg:grid-cols-12">
      <form onSubmit={submit} className="space-y-3 border border-white/10 bg-[#0d1524] p-6 lg:col-span-5" data-testid="partner-create-form">
        <p className="eyebrow">Issue partner access</p>
        <input className="field" required placeholder="Full name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} data-testid="partner-name-input" />
        <input className="field" required type="email" placeholder="Email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} data-testid="partner-email-input" />
        <input className="field" required placeholder="Company" value={f.company} onChange={(e) => setF({ ...f, company: e.target.value })} data-testid="partner-company-input" />
        <input className="field" required minLength={8} type="password" placeholder="Temporary password (8+)" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} data-testid="partner-password-input" />
        <button className="btn-primary w-full justify-center" data-testid="partner-create-button">Create account</button>
      </form>
      <div className="space-y-2 lg:col-span-7" data-testid="partner-list">
        {items.map((p) => (
          <div key={p.id} className="flex flex-wrap items-center justify-between gap-3 border-b border-white/10 py-3 text-sm" data-testid={`user-row-${p.id}`}>
            <span className="min-w-0">{p.name} <span className="text-slate-400">· {p.email}</span>
              <span className="ml-2 font-mono text-[10px] uppercase text-[var(--gold)]">{p.role}{p.company ? ` · ${p.company}` : ""}</span></span>
            <span className="flex items-center gap-2">
              <Badge s={p.status || "active"} testid={`user-status-${p.id}`} />
              {p.id !== me?.id && p.status !== "active" && (
                <>
                  <button onClick={() => setAccess(p, "active", "partner")} data-testid={`user-approve-partner-${p.id}`} className="rounded-full bg-white px-3 py-1 text-xs text-black hover:bg-[var(--gold)]">Approve partner</button>
                  <button onClick={() => setAccess(p, "active", "admin")} data-testid={`user-approve-admin-${p.id}`} className="rounded-full border border-white/20 px-3 py-1 text-xs hover:border-sky-400">Approve admin</button>
                </>
              )}
              {p.id !== me?.id && p.status !== "rejected" && (
                <button onClick={() => setAccess(p, "rejected", p.role)} data-testid={`user-reject-${p.id}`} className="rounded-full border border-rose-400/40 px-3 py-1 text-xs text-rose-300 hover:bg-rose-400/10">{p.status === "active" ? "Revoke" : "Reject"}</button>
              )}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Admin() {
  const { logout } = useAuth();
  const [tab, setTab] = useState("applications");
  const [stats, setStats] = useState({});
  const [apps, setApps] = useState([]);
  const [refs, setRefs] = useState([]);
  const [orders, setOrders] = useState([]);
  const [partners, setPartners] = useState([]);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    const arr = (r) => (Array.isArray(r.data) ? r.data : []);
    api.get("/admin/stats").then((r) => setStats(r.data || {})).catch(() => {});
    api.get("/applications").then((r) => setApps(arr(r))).catch(() => {});
    api.get("/referrals").then((r) => setRefs(arr(r))).catch(() => {});
    api.get("/admin/orders").then((r) => setOrders(arr(r))).catch(() => {});
    api.get("/admin/partners").then((r) => setPartners(arr(r))).catch(() => {});
    api.get("/admin/contacts").then((r) => setContacts(arr(r))).catch(() => {});
  }, []);

  const STATS = [["Applications", stats?.applications ?? 0], ["Approved", stats?.approved ?? 0], ["Referrals", stats?.referrals ?? 0], ["Paid orders", stats?.orders_paid ?? 0], ["Store revenue", money(stats?.revenue ?? 0)]];

  return (
    <main className="mx-auto max-w-[1400px] px-5 pb-24 pt-32 sm:px-10" data-testid="admin-page">
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div><p className="eyebrow">MidHq Admin</p><h1 className="mt-4 font-display text-4xl font-semibold tracking-tight sm:text-6xl">Operations desk.</h1></div>
        <div className="flex gap-2"><Link to="/portal" className="btn-ghost !py-2" data-testid="admin-portal-link">Corridor Portal</Link><Link to="/account" className="btn-ghost !py-2" data-testid="admin-account-link">Account</Link><button onClick={logout} className="btn-ghost !py-2" data-testid="admin-logout"><LogOut className="h-4 w-4" /></button></div>
      </div>
      <div className="mt-10 grid grid-cols-2 gap-px bg-white/10 md:grid-cols-5">
        {STATS.map(([l, v]) => (
          <div key={l} className="bg-[#080B11] p-5 last:col-span-2 md:last:col-span-1" data-testid={`admin-stat-${l.toLowerCase().replace(/\s+/g, "-")}`}>
            <p className="font-display text-3xl font-semibold">{v}</p><p className="mt-1 font-mono text-[11px] uppercase tracking-widest text-slate-400">{l}</p>
          </div>
        ))}
      </div>
      <div className="mt-10 flex flex-wrap gap-2">
        {TABS.map((t) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`} className={`relative rounded-full px-5 py-2.5 text-sm capitalize ${tab === t ? "text-black" : "text-slate-300"}`}>
            {tab === t && <motion.span layoutId="admin-tab-pill" className="absolute inset-0 rounded-full bg-white" />}
            <span className="relative">{TAB_LABEL[t] || t}{t === "partners" && partners.some((p) => p.status === "pending") ? ` (${partners.filter((p) => p.status === "pending").length})` : ""}{t === "inbox" && contacts.some((m) => m.status === "new") ? ` (${contacts.filter((m) => m.status === "new").length})` : ""}</span>
          </button>
        ))}
      </div>
      <div className="mt-8">
        {tab === "applications" && <Applications items={apps} setItems={setApps} />}
        {tab === "inbox" && <ContactInbox items={contacts} setItems={setContacts} />}
        {tab === "referrals" && <ReferralFeed items={refs} setItems={setRefs} />}
        {tab === "orders" && <Orders items={orders} />}
        {tab === "partners" && <Partners items={partners} setItems={setPartners} />}
        {tab === "activity" && <ActivityLog />}
      </div>
    </main>
  );
}
